import { Role } from '@today/types'
import { z } from 'zod'

// ─── Tenant SSO-config surface (/t/{slug}/admin/sso, TO-308) ─────────────────
// Shape only. The semantic guards — https-only metadata, the REQ-005
// superadmin / foreign-group refusal — run in the service, so they return their
// own machine-readable codes (IDP_METADATA_INVALID, IDP_MAPPING_REJECTED).

/** A SAML assertion attribute name. */
const samlAttributeName = z.string().trim().min(1).max(256)

/** Inline metadata is bounded again (in bytes) by the service; this caps the string early. */
const metadataSource = {
	metadataUrl: z.string().trim().min(1).max(2048).optional(),
	metadataXml: z
		.string()
		.min(1)
		.max(256 * 1024)
		.optional(),
}

const exactlyOneMetadataSource = (input: { metadataUrl?: string; metadataXml?: string }) =>
	(input.metadataUrl === undefined) !== (input.metadataXml === undefined)

const ONE_SOURCE = { message: 'Provide exactly one of metadataUrl or metadataXml' }

/** `PUT /t/{slug}/admin/sso` — attach or update the tenant's SAML IdP. */
export const tenantSsoConfigSchema = z
	.object({
		...metadataSource,
		attributeMapping: z
			.object({
				email: samlAttributeName,
				groups: samlAttributeName.optional(),
			})
			.strict(),
		targetGroup: z.string().trim().min(1).max(128).optional(),
	})
	.strict()
	.refine(exactlyOneMetadataSource, ONE_SOURCE)

/** `POST /t/{slug}/admin/sso/rotate` — replace the metadata only. */
export const tenantSsoRotateSchema = z.object(metadataSource).strict().refine(exactlyOneMetadataSource, ONE_SOURCE)

/** `POST /t/{slug}/admin/sso/test` — the test-login popup's authorization code + PKCE verifier. */
export const tenantSsoTestSchema = z
	.object({
		code: z.string().min(1).max(2048),
		codeVerifier: z.string().min(1).max(128),
		redirectUri: z.url().max(2048),
	})
	.strict()

/**
 * `PUT /t/{slug}/admin/sso/jit` (TO-335) — shape only. The role cap, ceiling,
 * domain format and the 50-domain limit (after de-duplication) run in the
 * service's pure normalizer so they return their own codes.
 */
export const tenantSsoJitSchema = z
	.object({
		enabled: z.boolean(),
		defaultRole: z.enum(Role).optional(),
		// Raw entries are bounded loosely here; the ≤ 50 rule applies after de-duplication.
		emailDomainAllowlist: z.array(z.string().max(512)).max(500).optional(),
	})
	.strict()

export type TenantSsoConfigInput = z.infer<typeof tenantSsoConfigSchema>
export type TenantSsoRotateInput = z.infer<typeof tenantSsoRotateSchema>
export type TenantSsoTestInput = z.infer<typeof tenantSsoTestSchema>
export type TenantSsoJitInput = z.infer<typeof tenantSsoJitSchema>
